import { useId, useRef, type ReactElement } from 'react';
import type { BitrixConnection } from '@chatapp-inbox/shared';
import { Button } from '../details/Button';
import { useFocusRescue } from '../details/use-focus-rescue';
import { BitrixConnectionSection } from './BitrixConnectionSection';
import { BitrixInstallSection } from './BitrixInstallSection';
import { BitrixOpenlinesSection } from './BitrixOpenlinesSection';
import { BitrixSettingsSection } from './BitrixSettingsSection';
import { adminErrorMessage } from './error-message';
import { SectionHeading } from './SectionHeading';
import { useBitrixConnection } from './use-bitrix-admin';
import section from './Section.module.css';

/** Conectada e com o ID e a chave do aplicativo guardados: dá para escolher as opções e ligar. */
const ready = (c: BitrixConnection): boolean =>
  c.status === 'connected' && c.clientId !== null && c.hasSecret;

/**
 * Bitrix24: o aplicativo local (OAuth) que dá ao CladvApp acesso ao CRM. Com ele, a mensagem de um cliente
 * vincula a conversa a um Lead ativo ou cria um Lead novo (docs/bitrix.md). Três partes: os dados do
 * aplicativo, o passo a passo da instalação (até conectar) e as opções (depois de conectado).
 */
export function BitrixScreen(): ReactElement {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bitrix = useBitrixConnection();

  // "Tentar de novo" some quando a conexão chega e levaria o foco ao <body>.
  const rescue = useFocusRescue(!bitrix.isError, headingRef);
  const retry = (): void => {
    rescue.arm();
    void bitrix.refetch().then((result) => {
      if (result.isError) rescue.disarm();
    });
  };
  const data = bitrix.data;

  return (
    <section aria-labelledby={headingId} className={section.screen}>
      <SectionHeading id={headingId} ref={headingRef}>
        Bitrix
      </SectionHeading>
      <p className={section.lead}>
        Quando um cliente escreve, o CladvApp procura o Lead dele no Bitrix24 e, se não houver um
        ativo, cria um na etapa inicial. A conexão é um aplicativo local do Bitrix, instalado uma
        vez.
      </p>

      {bitrix.isPending && (
        <p role="status" className={section.loading}>
          Carregando a conexão…
        </p>
      )}

      {bitrix.isError && data === undefined && (
        <div className={section.loadError}>
          <p role="alert" className={section.loadErrorText}>
            Não foi possível carregar a conexão com o Bitrix.{' '}
            {adminErrorMessage(bitrix.error, 'Tente de novo.')}
          </p>
          <Button busy={bitrix.isFetching} onClick={retry}>
            Tentar de novo
          </Button>
        </div>
      )}

      {data !== undefined && (
        <>
          {/* `key`: outra conexão (outro domínio, desconectada e refeita) recomeça o formulário do zero. */}
          <BitrixConnectionSection
            key={`${data.domain ?? ''}|${data.clientId ?? ''}|${data.hasSecret}`}
            connection={data}
          />
          {data.status !== 'not_configured' && !ready(data) && (
            <BitrixInstallSection connection={data} />
          )}
          {ready(data) && <BitrixSettingsSection connection={data} />}
          {ready(data) && <BitrixOpenlinesSection />}
        </>
      )}
    </section>
  );
}
